/* Currencies: what a new Organisation starts with, and how the full set is
 * presented.
 *
 * ── the list below is NOT the supported set ─────────────────────────────────
 * `i18n.md` forbids a committed list of "the currencies we support", and that
 * rule is unchanged: supported is every ISO 4217 code the runtime knows, read
 * from `Intl.supportedValuesOf('currency')`, and the create form offers all of
 * them.
 *
 * These five are the *enabled* set a new Organisation starts with — the handful
 * its everyday pickers offer, which an Owner edits from Settings without a
 * deploy. Decided 2026-08-16; see
 * `docs/superpowers/specs/2026-08-16-create-a-workspace-web-design.md`. It
 * reverses an earlier rule that a new Organisation enables exactly one, and
 * `i18n.md` and STORY-041 were corrected in the same change rather than left
 * disagreeing with this file.
 *
 * STORY-041 built the storage: `enabled_values`, one row per value per
 * Organisation. This constant is what `initialEnabledSet` seeds a new workspace
 * with, alongside its own currency if that falls outside the five — so it
 * decides a starting point and nothing after it. An Owner changes the set from
 * Settings, without a deploy.
 */
export const DEFAULT_ENABLED_CURRENCIES = ['USD', 'EUR', 'GBP', 'TRY', 'JPY'];
/**
 * Every currency the runtime knows, named and collated for one locale, with the
 * five defaults offered first.
 *
 * `common` keeps its declared order rather than being collated: it is a
 * shortlist somebody reads top to bottom, and re-ordering it per locale would
 * move the option a returning user reaches for by position.
 */
export function listCurrencies(locale) {
    const names = new Intl.DisplayNames([locale], { type: 'currency' });
    const nameOf = (code) => names.of(code) ?? code;
    /* Locale-aware collation, never byte order — `i18n.md`. Byte order puts every
     * accented name after Z, which in Turkish is most of the list. */
    const collator = new Intl.Collator(locale);
    const all = Intl.supportedValuesOf('currency')
        .map((code) => ({ code, name: nameOf(code) }))
        .sort((a, b) => collator.compare(a.name, b.name));
    return {
        common: DEFAULT_ENABLED_CURRENCIES.map((code) => ({ code, name: nameOf(code) })),
        all,
    };
}
/**
 * The options whose code or name contains the query.
 *
 * The lowercasing carries an explicit locale on BOTH sides, which is not
 * ceremony. Turkish has a dotted and a dotless i, so capital `I` folds to `ı`
 * here and to `i` everywhere else: a Turkish speaker searching "ırak" finds the
 * Iraqi Dinar only if its name — "Irak dinarı" — was folded by the same rules.
 * `i18n.md` calls casing without an explicit locale a defect, and this is the
 * call site that proves why.
 */
export function filterCurrencies(options, query, locale) {
    const needle = query.trim().toLocaleLowerCase(locale);
    if (needle === '')
        return options;
    /* The ISO code is locale-invariant ASCII and is folded as such. Folding it
     * with the READER's locale turned ILS into "ıls" under Turkish while the typed
     * needle folded to "ils", and "ıls".includes("ils") is false — so ILS, IQD,
     * INR, ISK and IDR became unreachable by their own codes for exactly the
     * readers most likely to want IQD and TRY. The same needle has to be folded
     * both ways for the same reason: a Turkish reader typing "ils" produces a
     * dotted i, and the code side must meet it there. */
    const asciiNeedle = query.trim().toLocaleLowerCase('en-US');
    return options.filter((option) => option.code.toLocaleLowerCase('en-US').includes(asciiNeedle) ||
        option.name.toLocaleLowerCase(locale).includes(needle));
}
//# sourceMappingURL=currencies.js.map